/**
 * Shared helpers for parsing API error envelopes on the public surface.
 *
 * The BE wraps every error in `{ error: { code, message, details } }`
 * (see `app/shared/errors.py`). This module exposes a typed mapper so
 * each page can decide whether to:
 *   - render a dedicated error state (404 → "form not configured")
 *   - surface inline field errors (422 with details.field_errors)
 *   - or fall back to a generic toast/banner.
 */
import { isAxiosError } from "axios";
import type { ApiErrorEnvelope, FieldValidationError } from "@eduviaos/shared/types";

export type PublicErrorKind =
  | "tenant_or_form_not_found"
  | "rate_limited"
  | "validation"
  | "server_error"
  | "network"
  | "unknown";

export interface PublicError {
  kind: PublicErrorKind;
  status: number | null;
  code: string | null;
  message: string;
  fieldErrors: FieldValidationError[];
  /** Phase H2 — pass through the BE error envelope's `details` blob so
   * surfaces that need typed sub-fields (e.g. the apply-portal
   * `ADMISSIONS_CLOSED` empty-state needs `details.next_window`,
   * `details.reason`, `details.closed_message_html`) can read them
   * without re-parsing the axios error. Keys vary by error code; the
   * caller knows which shape to expect from `code`. */
  details: Record<string, unknown> | null;
}

/**
 * Convert a thrown axios error into a structured `PublicError`.
 *
 * Distinguishes the four cases the parent UX cares about:
 *   - 404 → tenant unknown / form not configured / token invalid
 *   - 429 → rate-limited (parent submitted too fast)
 *   - 422 / 4xx with field details → render inline against fields
 *   - everything else → friendly "something went wrong" with a request
 *     id if available
 */
export function parsePublicError(err: unknown): PublicError {
  if (!isAxiosError(err)) {
    return {
      kind: "unknown",
      status: null,
      code: null,
      message: "Something went wrong. Please try again.",
      fieldErrors: [],
      details: null,
    };
  }

  if (!err.response) {
    return {
      kind: "network",
      status: null,
      code: null,
      message:
        "We couldn't reach EduViaOS. Check your internet connection and try again — your form data is still safe in this tab.",
      fieldErrors: [],
      details: null,
    };
  }

  const status = err.response.status;
  const envelope = err.response.data as ApiErrorEnvelope | undefined;
  const code = envelope?.error?.code ?? null;
  const message =
    envelope?.error?.message ??
    "Something went wrong. Please try again, or contact the institution directly.";
  const rawDetails = (envelope?.error?.details ?? null) as Record<string, unknown> | null;

  if (status === 404) {
    return {
      kind: "tenant_or_form_not_found",
      status,
      code,
      message,
      fieldErrors: [],
      details: rawDetails,
    };
  }
  if (status === 429) {
    return {
      kind: "rate_limited",
      status,
      code,
      message: "Too many submissions from your network. Please wait a minute and try again.",
      fieldErrors: [],
      details: rawDetails,
    };
  }
  if (status >= 400 && status < 500) {
    const details = envelope?.error?.details as
      | { field_errors?: FieldValidationError[] }
      | undefined;
    return {
      kind: "validation",
      status,
      code,
      message,
      fieldErrors: details?.field_errors ?? [],
      details: rawDetails,
    };
  }
  return {
    kind: "server_error",
    status,
    code,
    message,
    fieldErrors: [],
    details: rawDetails,
  };
}
